import { handleLogout, msalInstance } from '@/utils/auth'
import { getKONGToken } from '@/utils/auth/tokenFetcher'
import { API_URL, USE_KONG_API } from '@/utils/env'
import { CLInstance } from '@/SDK'
import { ClientEventType } from '@/SDK/ClientLogger'

/**
 * Custom fetch instance for Orval-generated API client.
 * Orval calls this as: customInstance<T>(url, requestInit)
 */
export const customInstance = async <T>(
  url: string,
  init?: RequestInit,
): Promise<T> => {
  const account = msalInstance.getActiveAccount()

  if (!account) {
    throw new Error('No active account! Verify a user has been signed in and setActiveAccount has been called.')
  }

  const headers = new Headers(init?.headers)

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: [],
      account,
    })

    if (USE_KONG_API) {
      const token = await getKONGToken()
      headers.set('Authorization', `Bearer ${token}`)
    }

    headers.set('MsalAuthorization', `Bearer ${response.accessToken}`)

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
  } catch (error) {
    console.error('Error acquiring token:', error)
    handleLogout()
    throw error
  }

  // Prepend API_URL to relative paths from Orval-generated endpoints
  const fullUrl = url.startsWith('http') ? url : `${API_URL.replace(/\/$/, '')}${url}`

  const res = await fetch(fullUrl, {
    ...init,
    headers,
  })

  if (res.ok) {
    if (res.status === 204) {
      return { data: undefined, status: 204, headers: res.headers } as T
    }
    const data = await res.json()
    return { data, status: res.status, headers: res.headers } as T
  }

  // Global 401 handler
  if (res.status === 401) {
    CLInstance.log(ClientEventType.ERROR, {
      url,
      status: 401,
      message: 'Unauthorized',
    })
    if (USE_KONG_API) {
      await getKONGToken()
    }
    try {
      window.location.href = '/401?cause=endpoint'
    } catch {
      window.location.href = '/401'
    }
  }

  // Log API errors
  CLInstance.log(ClientEventType.ERROR, {
    url,
    status: res.status,
    message: 'API error',
  })

  // Throw structured error
  const errorBody = await res.json().catch(() => ({ message: 'Unknown error' }))
  const error = new Error(
    typeof errorBody?.message === 'string' ? errorBody.message : JSON.stringify(errorBody),
    { cause: res.status }
  )
  ;(error as any).status = res.status
  ;(error as any).data = errorBody
  throw error
}

export default customInstance
